/**
 * ActuatorComponent
 * - display actuator status
 * - display device info
 * - control position value with slider and numeric input
 */

import React, { useEffect, useState } from 'react';

/* MUI */
import TextField from '@mui/material/TextField';
import { Slider } from '../residential-devices/thermostat/components/system-edit/slider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Icon } from '@iconify/react';

/* Types */
import {
  Actuator,
  Property,
  useActuatorDetailQuery,
  useActuatorDetailUpdateSubscription,
  useUpdateActuatorPositionMutation,
} from '../../../../types/generated-types';

/* Shared */
import { DeviceInformation } from '../shared-ui/device-info';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import moment from 'moment/moment';
import { Notifier } from '../../../system/services/notificationManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { PanelFrame, ViewMode, MenuAction } from '../shared-ui/panel-frame';

interface ActuatorProps {
  deviceId: string;
}

export function ActuatorComponent({ deviceId }: ActuatorProps) {
  const [device, setDevice] = useState<Partial<Actuator>>();
  const { data, loading, error } = useActuatorDetailQuery({
    variables: { id: deviceId },
  });
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);
  const [updatePosition] = useUpdateActuatorPositionMutation();
  const { can } = useAuthorizer();

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.NORMAL);
  const [editedPosition, setEditedPosition] = useState<number | null>(null);
  const [originalPosition, setOriginalPosition] = useState<number | null>(null);
  const [editedControlMode, setEditedControlMode] = useState<string | null>(
    null,
  );
  const [originalControlMode, setOriginalControlMode] = useState<string | null>(
    null,
  );
  const [updatingState, setUpdatingState] = useState<
    'Updating' | 'Awaiting' | 'Blocked' | 'Idle'
  >('Idle');

  useActuatorDetailUpdateSubscription({
    variables: { ids: [deviceId] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.actuatorById) {
      const deviceData = data.actuatorById as any;
      setDevice(deviceData);
      const currentPosition = deviceData.position?.value ?? null;
      // Round to integer since position is an integer metric
      const roundedPosition =
        currentPosition !== null ? Math.round(currentPosition) : null;
      const currentControlMode = deviceData.controlMode?.value ?? 'Auto';
      if (originalPosition === null) {
        setOriginalPosition(roundedPosition);
        setEditedPosition(roundedPosition);
        setOriginalControlMode(currentControlMode);
        setEditedControlMode(currentControlMode);
      } else if (viewMode === ViewMode.NORMAL) {
        // Update position and controlMode values when device updates in normal (read-only) mode
        setOriginalPosition(roundedPosition);
        setEditedPosition(roundedPosition);
        setOriginalControlMode(currentControlMode);
        setEditedControlMode(currentControlMode);
      }
    }
  }, [data, originalPosition, viewMode]);

  useEffect(() => {
    if (device) {
      // Parse units from meta.metrics.position.units
      let positionUnitsForInfo = '%'; // Default units
      try {
        const metrics = device.meta?.metrics;
        let positionMetric: any = undefined;

        if (metrics) {
          if (typeof metrics === 'object' && metrics !== null) {
            if ('position' in metrics) {
              positionMetric = (metrics as any).position;
            } else if (typeof (metrics as any).get === 'function') {
              positionMetric = (metrics as any).get('position');
            }
          } else if (typeof metrics === 'string') {
            try {
              const parsed = JSON.parse(metrics);
              positionMetric = parsed?.position;
            } catch {
              // Not JSON, ignore
            }
          }

          if (positionMetric && typeof positionMetric === 'object') {
            if (
              positionMetric.units &&
              typeof positionMetric.units === 'string'
            ) {
              positionUnitsForInfo = positionMetric.units;
            }
          }
        }
      } catch (error) {
        // Use default '%' if parsing fails
      }

      const panelInfo: DeviceInformation = {
        Panel: device.panel?.name ?? 'Unnamed',
        'Panel ID': device.panelId ?? 'Unidentified',
        'Panel Type': device.panel?.type ?? 'Unknown',
        Source: device.source?.name ?? 'Unnamed source',
      };

      const devicesInfo: DeviceInformation[] = [
        {
          'Device ID': device.deviceId ?? device._id ?? 'Unidentified',
          Name: device.name ?? 'Unnamed',
          Type: device.typeDisplayName ?? 'Unknown',
          'Last Updated': moment(device.timestamp).fromNow(),
          Position: device.position?.value
            ? `${device.position.value} ${positionUnitsForInfo}`
            : 'N/A',
          'Default Position': device.defaultPosition?.value
            ? `${device.defaultPosition.value} ${positionUnitsForInfo}`
            : `40 ${positionUnitsForInfo}`,
        },
      ];

      setDeviceInfo([panelInfo, ...devicesInfo]);
    }
  }, [device]);

  // Check if user can manage the property
  const canManageProperty = device?.propertyId
    ? can('manage', { _id: device.propertyId } as Partial<Property>)
    : false;

  // Check if position value has changed (integer comparison)
  const hasPositionChanged =
    editedPosition !== null &&
    originalPosition !== null &&
    Math.round(editedPosition) !== Math.round(originalPosition);

  // Check if controlMode value has changed
  const hasControlModeChanged =
    editedControlMode !== null &&
    originalControlMode !== null &&
    editedControlMode !== originalControlMode;

  // Check if any values have changed
  const hasChanges = hasPositionChanged || hasControlModeChanged;

  // Determine edit mode state (needed before positionControlsEnabled calculation)
  const isEditMode =
    viewMode === ViewMode.EDIT || viewMode === ViewMode.EDIT_UNCHANGED;
  const isUpdating = viewMode === ViewMode.UPDATING;

  // Determine if position controls should be enabled (only when in Hand mode)
  const isHandMode = editedControlMode === 'Hand';
  const positionControlsEnabled = isEditMode && isHandMode;

  // Get current control mode for display (use device value when not editing)
  const currentControlMode =
    viewMode === ViewMode.NORMAL
      ? (device?.controlMode?.value ?? 'Auto')
      : (editedControlMode ?? 'Auto');

  // Handle menu button clicks
  const handleMenuButtonClick = async (action: MenuAction) => {
    switch (action) {
      case MenuAction.EDIT_VALUES:
        // Enter edit mode - no values changed yet
        setViewMode(ViewMode.EDIT_UNCHANGED);
        break;
      case MenuAction.CANCEL:
      case MenuAction.REVERT_CHANGES:
        // Exit edit mode - revert to original values
        setEditedPosition(originalPosition);
        setEditedControlMode(originalControlMode);
        setViewMode(ViewMode.NORMAL);
        break;
      case MenuAction.SAVE_CHANGES:
        // Save the position and controlMode values
        if (
          !device?.deviceId ||
          editedPosition === null ||
          editedControlMode === null
        ) {
          return;
        }
        // Special case: If original controlMode was 'Auto' and user unchecks before saving,
        // treat it as a cancel (revert to original values and exit edit mode)
        // This handles the case where user checked 'Hand Mode' then unchecked it before saving
        if (
          originalControlMode === 'Auto' &&
          editedControlMode === 'Auto' &&
          !hasPositionChanged
        ) {
          setEditedPosition(originalPosition);
          setEditedControlMode(originalControlMode);
          setViewMode(ViewMode.NORMAL);
          return;
        }
        setUpdatingState('Updating');
        setViewMode(ViewMode.UPDATING);
        try {
          const result = await updatePosition({
            variables: {
              deviceId: device.deviceId,
              position: editedPosition,
              controlMode: editedControlMode,
            } as any, // Type will be updated after schema regeneration
          });

          // Update state from mutation response
          if (result.data?.updateActuatorPosition) {
            const updatedDevice = result.data.updateActuatorPosition;
            const responsePosition = updatedDevice.position?.value;
            const responseControlMode = updatedDevice.controlMode?.value;

            console.log('[Actuator Update] Mutation response:', {
              responsePosition,
              responseControlMode,
              fullResponse: updatedDevice,
            });

            // Update from response if available, otherwise use edited values
            if (responsePosition !== undefined && responsePosition !== null) {
              const roundedPosition = Math.round(responsePosition);
              setOriginalPosition(roundedPosition);
              setEditedPosition(roundedPosition);
            } else {
              setOriginalPosition(editedPosition);
            }

            if (
              responseControlMode !== undefined &&
              responseControlMode !== null
            ) {
              setOriginalControlMode(responseControlMode);
              setEditedControlMode(responseControlMode);
            } else {
              console.warn(
                '[Actuator Update] controlMode is null/undefined in response, using edited value:',
                editedControlMode,
              );
              setOriginalControlMode(editedControlMode);
              setEditedControlMode(editedControlMode);
            }
          } else {
            // Fallback to edited values if response doesn't have data
            console.warn(
              '[Actuator Update] No data in mutation response, using edited values',
            );
            setOriginalPosition(editedPosition);
            setOriginalControlMode(editedControlMode);
          }

          // Note: ViewMode and updatingState will be updated by the hasPendingUpdates effect
          // when the update completes
        } catch (updateError) {
          setUpdatingState('Idle');
          console.error('Error updating actuator:', updateError);
          Notifier.error('Failed to update actuator');
          setViewMode(ViewMode.EDIT);
        }
        break;
    }
  };

  // Handle slider change - clamp to value limits and round to integer
  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    // Round to integer since position is an integer metric
    const roundedValue = Math.round(value);
    // Clamp value to the settable range (value limits, not display limits)
    const clampedValue = Math.max(
      minValuePosition,
      Math.min(maxValuePosition, roundedValue),
    );
    setEditedPosition(clampedValue);
    if (viewMode === ViewMode.EDIT_UNCHANGED) {
      setViewMode(ViewMode.EDIT);
    }
  };

  // Handle text field change - clamp to value limits and round to integer
  const handleTextFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      // Round to integer since position is an integer metric
      const roundedValue = Math.round(value);
      // Clamp value to the settable range (value limits, not display limits)
      const clampedValue = Math.max(
        minValuePosition,
        Math.min(maxValuePosition, roundedValue),
      );
      setEditedPosition(clampedValue);
      if (viewMode === ViewMode.EDIT_UNCHANGED) {
        setViewMode(ViewMode.EDIT);
      }
    }
  };

  // Handle controlMode checkbox change
  const handleControlModeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.target.checked;
    const newControlMode = checked ? 'Hand' : 'Auto';
    setEditedControlMode(newControlMode);
    // If switching from Hand to Auto, we might want to keep the position value
    // but it will be disabled for editing
    if (viewMode === ViewMode.EDIT_UNCHANGED) {
      setViewMode(ViewMode.EDIT);
    }
  };

  // Update view mode when position or controlMode changes
  useEffect(() => {
    if (viewMode === ViewMode.EDIT_UNCHANGED && hasChanges) {
      setViewMode(ViewMode.EDIT);
    } else if (viewMode === ViewMode.EDIT && !hasChanges) {
      setViewMode(ViewMode.EDIT_UNCHANGED);
    }
  }, [hasChanges, viewMode]);

  // Handle updating state based on hasPendingUpdates
  useEffect(() => {
    if (device?.hasPendingUpdates) {
      if (updatingState === 'Idle') {
        setUpdatingState('Blocked');
        Notifier.warn('Someone else is updating this device.');
      } else if (updatingState === 'Updating') {
        setUpdatingState('Awaiting');
      }
    } else {
      if (updatingState === 'Updating' || updatingState === 'Blocked') {
        setUpdatingState('Idle');
        // Reset viewMode to NORMAL when update completes
        if (viewMode === ViewMode.UPDATING) {
          setViewMode(ViewMode.NORMAL);
        }
      } else if (updatingState === 'Awaiting') {
        setUpdatingState('Idle');
        // Reset viewMode to NORMAL when update completes
        if (viewMode === ViewMode.UPDATING) {
          setViewMode(ViewMode.NORMAL);
        }
        Notifier.success('Device update completed.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.hasPendingUpdates]);

  if (loading) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  if (error || !device) {
    return (
      <div>Error: {error ? JSON.stringify(error) : 'Device not found'}</div>
    );
  }

  const currentPosition = device.position?.value ?? 0;
  // Round to integer since position is an integer metric
  const roundedCurrentPosition = Math.round(currentPosition);
  const displayPosition =
    editedPosition !== null ? editedPosition : roundedCurrentPosition;

  // Determine slider display range from meta.metrics.position.displayValue (format: '[LOW, HIGH]')
  // and settable value range from meta.metrics.position.value (format: '[LOW, HIGH]')
  // Also get units from meta.metrics.position.units
  // Also get step from meta.metrics.position.valueStep
  // Default to 0-100% if not specified or invalid
  let minDisplayPosition = 0;
  let maxDisplayPosition = 100;
  let minValuePosition = 0;
  let maxValuePosition = 100;
  let positionUnits = '%'; // Default units
  let positionStep = 1; // Default step (integer)
  try {
    // meta.metrics is a Map in Mongoose, which may be exposed as JSON or nested object
    const metrics = device.meta?.metrics;
    let positionMetric: any = undefined;

    // Try different ways to access the metrics Map
    if (metrics) {
      // If metrics is a Map-like object, try direct access
      if (typeof metrics === 'object' && metrics !== null) {
        // Try accessing as nested object (e.g., metrics.position)
        if ('position' in metrics) {
          positionMetric = (metrics as any).position;
        }
        // If metrics is a Map instance, try using .get() method
        else if (typeof (metrics as any).get === 'function') {
          positionMetric = (metrics as any).get('position');
        }
      }
      // If metrics is a JSON string, parse it
      else if (typeof metrics === 'string') {
        try {
          const parsed = JSON.parse(metrics);
          positionMetric = parsed?.position;
        } catch {
          // Not JSON, ignore
        }
      }
    }

    // Parse position metric data
    if (positionMetric && typeof positionMetric === 'object') {
      // Get units from meta.metrics.position.units
      if (positionMetric.units && typeof positionMetric.units === 'string') {
        positionUnits =
          positionMetric.units === 'percent' ? '%' : positionMetric.units;
      }

      // Get step from meta.metrics.position.valueStep
      if (
        positionMetric.valueStep !== undefined &&
        positionMetric.valueStep !== null
      ) {
        const parsedStep = parseFloat(String(positionMetric.valueStep));
        if (!isNaN(parsedStep) && parsedStep > 0) {
          // Round to integer since position is an integer metric
          positionStep = Math.round(parsedStep);
        }
      }

      // Parse displayValue for slider range
      const displayValueStr = positionMetric.displayValue;
      if (displayValueStr && typeof displayValueStr === 'string') {
        const displayMatch = displayValueStr.match(/\[([\d.]+),\s*([\d.]+)\]/);
        if (displayMatch && displayMatch.length === 3) {
          const parsedMin = parseFloat(displayMatch[1]);
          const parsedMax = parseFloat(displayMatch[2]);
          if (!isNaN(parsedMin) && !isNaN(parsedMax) && parsedMin < parsedMax) {
            minDisplayPosition = parsedMin;
            maxDisplayPosition = parsedMax;
          }
        }
      }

      // Parse value for settable range
      const valueStr = positionMetric.value;
      if (valueStr && typeof valueStr === 'string') {
        const valueMatch = valueStr.match(/\[([\d.]+),\s*([\d.]+)\]/);
        if (valueMatch && valueMatch.length === 3) {
          const parsedMin = parseFloat(valueMatch[1]);
          const parsedMax = parseFloat(valueMatch[2]);
          if (!isNaN(parsedMin) && !isNaN(parsedMax) && parsedMin < parsedMax) {
            minValuePosition = parsedMin;
            maxValuePosition = parsedMax;
          }
        }
      }
    } else {
      // Debug: log when we can't find the position metric
      console.log('Actuator: Could not find position metric in meta.metrics', {
        hasMeta: !!device.meta,
        hasMetrics: !!device.meta?.metrics,
        metricsType: typeof device.meta?.metrics,
        metrics: device.meta?.metrics,
      });
    }
  } catch (error) {
    console.warn(
      'Error parsing position limits from meta.metrics.position:',
      error,
      'device.meta:',
      device.meta,
    );
  }

  // Calculate percentage positions for the settable range boundaries
  const displayRange = maxDisplayPosition - minDisplayPosition;
  const lowerGreyEndPercent =
    displayRange > 0
      ? ((minValuePosition - minDisplayPosition) / displayRange) * 100
      : 0;
  const upperGreyStartPercent =
    displayRange > 0
      ? ((maxValuePosition - minDisplayPosition) / displayRange) * 100
      : 100;

  // Calculate the current value's position as a percentage
  const currentValuePercent =
    displayRange > 0
      ? ((displayPosition - minDisplayPosition) / displayRange) * 100
      : 0;

  // Color constants - both non-settable ranges use the same light grey shade
  const lightGreyColor = 'rgba(0, 0, 0, 0.12)'; // Light grey for non-settable ranges
  const darkGreyColor = 'rgba(0, 0, 0, 0.87)'; // Dark grey/black for settable range

  return (
    <PanelFrame
      iconType={
        device.panel?.role === 'Valve'
          ? 'material-symbols:valve-outline'
          : 'f7:dial'
      }
      panel={device.panel}
      device={device as any}
      infoEntries={deviceInfo}
      viewMode={viewMode}
      label={device.name ?? undefined}
      editingEnabled={!isUpdating && !device.hasPendingUpdates}
      editable={canManageProperty}
      handleMenuButtonClick={handleMenuButtonClick}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0px 20px',
          gap: '8px',
          minWidth: 0, // Allow flex children to shrink below their content size
        }}
      >
        {['Updating', 'Awaiting', 'Blocked'].includes(updatingState) ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '16px 0',
            }}
          >
            <CircularProgress size={16} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              Updating...
            </Typography>
          </Box>
        ) : (
          <>
            {isEditMode && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isHandMode}
                    onChange={handleControlModeChange}
                    disabled={isUpdating || device.hasPendingUpdates}
                    size="small"
                  />
                }
                label="Hand Mode"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                  },
                  flexShrink: 0,
                  marginRight: '4px',
                }}
              />
            )}
            {!isEditMode && (
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: '8px',
                }}
              >
                <Icon
                  icon={
                    currentControlMode === 'Hand'
                      ? 'material-symbols:hand-gesture'
                      : 'material-symbols:auto-mode'
                  }
                  width="20"
                  height="20"
                  color={
                    currentControlMode === 'Hand'
                      ? '#1976d2'
                      : 'rgba(0, 0, 0, 0.6)'
                  }
                />
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                minWidth: 0, // Allow flex item to shrink below content size
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Slider
                value={displayPosition}
                min={minDisplayPosition}
                max={maxDisplayPosition}
                step={positionStep}
                onChange={handleSliderChange}
                disabled={
                  !positionControlsEnabled ||
                  isUpdating ||
                  device.hasPendingUpdates
                }
                valueLabelDisplay={isEditMode ? 'off' : 'auto'}
                valueLabelFormat={(value: number) =>
                  `${Math.round(value)} ${positionUnits}`
                }
                sx={{
                  flex: 1,
                  minWidth: 0, // Allow slider to shrink
                  // Style the rail (background track) with light grey for non-settable ranges
                  // and dark grey/black for settable range
                  // Both lower and upper non-settable ranges use the same light grey shade
                  '& .MuiSlider-rail': {
                    background: `linear-gradient(to right,
                      ${lightGreyColor} 0%,
                      ${lightGreyColor} ${lowerGreyEndPercent}%,
                      ${darkGreyColor} ${lowerGreyEndPercent}%,
                      ${darkGreyColor} ${upperGreyStartPercent}%,
                      ${lightGreyColor} ${upperGreyStartPercent}%,
                      ${lightGreyColor} 100%
                    )`,
                    opacity: 1,
                    height: '4px', // Track thickness
                  },
                  // Make thumb smaller in read-only mode (about 3x track thickness = 12px diameter)
                  '& .MuiSlider-thumb': {
                    ...(isEditMode
                      ? {
                          // Normal size in edit mode (default from custom Slider component)
                          height: 32,
                          width: 32,
                        }
                      : {
                          // Smaller dot in read-only mode (about 3x track thickness = 12px)
                          height: 12,
                          width: 12,
                          border: 'none',
                          boxShadow: 'none',
                          backgroundColor: darkGreyColor, // Filled circle matching track color
                          '&:hover': {
                            boxShadow: 'none',
                          },
                          '&.Mui-focusVisible': {
                            boxShadow: 'none',
                          },
                          '&.Mui-active': {
                            boxShadow: 'none',
                          },
                        }),
                  },
                  // Style the track (active portion) to show light grey for non-settable portions
                  // and dark grey/black for settable portions
                  // Both lower and upper non-settable ranges use the same light grey shade
                  // The track spans from minDisplayPosition to currentValue, so we need to show
                  // light grey for the portion below minValuePosition and dark grey for the portion above
                  '& .MuiSlider-track': {
                    background: (() => {
                      // If current value is below the settable range, show all light grey
                      if (currentValuePercent <= lowerGreyEndPercent) {
                        return lightGreyColor;
                      }
                      // If current value is above the settable range
                      if (currentValuePercent >= upperGreyStartPercent) {
                        // Track goes from 0% to currentValuePercent of full slider
                        // Within the track: light grey from 0% to lowerGreyEndPercent, dark grey to upperGreyStartPercent, light grey to end
                        const lightGreyStartInTrack =
                          (lowerGreyEndPercent / currentValuePercent) * 100;
                        const darkGreyEndInTrack =
                          (upperGreyStartPercent / currentValuePercent) * 100;
                        return `linear-gradient(to right,
                          ${lightGreyColor} 0%,
                          ${lightGreyColor} ${lightGreyStartInTrack}%,
                          ${darkGreyColor} ${lightGreyStartInTrack}%,
                          ${darkGreyColor} ${darkGreyEndInTrack}%,
                          ${lightGreyColor} ${darkGreyEndInTrack}%,
                          ${lightGreyColor} 100%
                        )`;
                      }
                      // Current value is in the settable range
                      // Track goes from 0% to currentValuePercent of full slider
                      // Within the track: light grey from 0% to lowerGreyEndPercent, dark grey to end
                      const lightGreyPortionInTrack =
                        (lowerGreyEndPercent / currentValuePercent) * 100;
                      return `linear-gradient(to right,
                        ${lightGreyColor} 0%,
                        ${lightGreyColor} ${lightGreyPortionInTrack}%,
                        ${darkGreyColor} ${lightGreyPortionInTrack}%,
                        ${darkGreyColor} 100%
                      )`;
                    })(),
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '4px',
                minWidth: 'fit-content',
              }}
            >
              {isEditMode ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  <TextField
                    type="number"
                    value={Math.round(displayPosition)}
                    onChange={handleTextFieldChange}
                    disabled={
                      !positionControlsEnabled ||
                      isUpdating ||
                      device.hasPendingUpdates
                    }
                    inputProps={{
                      step: positionStep,
                      min: minValuePosition,
                      max: maxValuePosition,
                      style: { textAlign: 'right' },
                    }}
                    sx={{
                      width: '45px',
                      flexShrink: 0,
                      '& .MuiInputBase-input': {
                        padding: '4px 4px',
                        fontSize: '16px',
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: 'text.secondary',
                    }}
                  >
                    {positionUnits}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: 'text.primary',
                    }}
                  >
                    {Math.round(displayPosition)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: 'text.secondary',
                    }}
                  >
                    {positionUnits}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </PanelFrame>
  );
}
